import { UnifiedUser } from "@/types/unified-user";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";

interface UserDetailsTabProps {
  user: UnifiedUser;
}

export function UserDetailsTab({ user }: UserDetailsTabProps) {
  const isDirectory = user.type === 'directory' || user.type === 'both';
  const isConsole = user.type === 'console' || user.type === 'both';

  return (
    <div className="space-y-8">
      {/* Directory Information */}
      {isDirectory && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Directory Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {user.username && (
              <div>
                <p className="text-sm text-muted-foreground">Username</p>
                <p className="font-medium mt-1">{user.username}</p>
              </div>
            )}

            {user.email && (
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium mt-1">{user.email}</p>
              </div>
            )}

            {user.firstName && (
              <div>
                <p className="text-sm text-muted-foreground">First Name</p>
                <p className="font-medium mt-1">{user.firstName}</p>
              </div>
            )}

            {user.lastName && (
              <div>
                <p className="text-sm text-muted-foreground">Last Name</p>
                <p className="font-medium mt-1">{user.lastName}</p>
              </div>
            )}

            {user.department && (
              <div>
                <p className="text-sm text-muted-foreground">Department</p>
                <p className="font-medium mt-1">{user.department}</p>
              </div>
            )}

            {user.title && (
              <div>
                <p className="text-sm text-muted-foreground">Title</p>
                <p className="font-medium mt-1">{user.title}</p>
              </div>
            )}

            {user.provider && (
              <div>
                <p className="text-sm text-muted-foreground">Identity Provider</p>
                <Badge variant="outline" className="mt-1">{user.provider}</Badge>
              </div>
            )}

            {user.customer && (
              <div>
                <p className="text-sm text-muted-foreground">Customer</p>
                <p className="font-medium mt-1">{user.customer}</p>
              </div>
            )}

            {user.managerEmail && (
              <div>
                <p className="text-sm text-muted-foreground">Manager</p>
                <p className="font-medium mt-1">{user.managerEmail}</p>
              </div>
            )}

            {user.externalId && (
              <div>
                <p className="text-sm text-muted-foreground">External ID</p>
                <p className="font-medium font-mono text-sm mt-1">{user.externalId}</p>
              </div>
            )}

            {user.syncedAt && (
              <div>
                <p className="text-sm text-muted-foreground">Last Synced</p>
                <p className="font-medium mt-1">
                  {formatDistanceToNow(new Date(user.syncedAt), { addSuffix: true })}
                </p>
              </div>
            )}

            {user.lastActive && (
              <div>
                <p className="text-sm text-muted-foreground">Last Active</p>
                <p className="font-medium mt-1">
                  {formatDistanceToNow(new Date(user.lastActive), { addSuffix: true })}
                </p>
              </div>
            )}
          </div>

          {/* Custom Attributes */}
          {user.attributes && Object.keys(user.attributes).length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-3">Custom Attributes</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(user.attributes).map(([key, value]) => (
                  <Badge key={key} variant="secondary">
                    {key}: {String(value)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Console Information */}
      {isConsole && (
        <div>
          {isDirectory && <div className="border-t border-border" />}
          <h3 className="text-lg font-semibold mb-4 mt-8">Console Access</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {user.roles && user.roles.length > 0 && (
              <div className="md:col-span-2 lg:col-span-3">
                <p className="text-sm text-muted-foreground">Roles</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {user.roles.map((userRole) => (
                    <Badge key={userRole.id} variant="secondary">
                      {userRole.role?.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {user.joinDate && (
              <div>
                <p className="text-sm text-muted-foreground">Console Join Date</p>
                <p className="font-medium mt-1">
                  {formatDistanceToNow(new Date(user.joinDate), { addSuffix: true })}
                </p>
              </div>
            )}

            {user.email && (
              <div>
                <p className="text-sm text-muted-foreground">Console Email</p>
                <p className="font-medium mt-1">{user.email}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity Summary */}
      <div>
        <div className="border-t border-border" />
        <h3 className="text-lg font-semibold mb-4 mt-8">Activity Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
          <div>
            <p className="text-sm text-muted-foreground">Status</p>
            <div className="mt-1">
              <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                {user.status}
              </Badge>
            </div>
          </div>

          {user.deviceCount > 0 && (
            <div>
              <p className="text-sm text-muted-foreground">Enrolled Devices</p>
              <p className="text-2xl font-bold mt-1">{user.deviceCount}</p>
            </div>
          )}

          {user.lastActive && (
            <div>
              <p className="text-sm text-muted-foreground">Last Activity</p>
              <p className="font-medium mt-1">
                {formatDistanceToNow(new Date(user.lastActive), { addSuffix: true })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
